import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom/cjs/react-router-dom";
import { Avatar, Box, Button, Container, Divider, Grid, IconButton, Typography, Paper, Stack, Chip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDirections, faLink, faLocationDot } from "@fortawesome/free-solid-svg-icons";
import { faFacebookF, faTelegram, faTwitter, faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import moment from "moment";
import 'moment/locale/es';

import ShareButton from "../../../components/ShareButton";
import { FacebookShareButton, TelegramShareButton, TwitterShareButton, WhatsappShareButton } from "react-share";
import ReactQuill from "react-quill";
import 'react-quill/dist/quill.snow.css';
import ReactPlayer from "react-player";
import '../../../App.css'

import { domain, imagesURL } from "../../../apiRoutes/apiBase";
import QUDProgress from "../../../components/progress";
import ResponseComponent from "../../../components/responseComponent";
import { formatStringWithHyphens } from "../../../controllers/AuxController";
import { Helmet, HelmetData } from "react-helmet-async";
import { createView } from "../../../controllers/customer/ViewsController";
import { GetEventByID } from "../../../controllers/customer/EventosController";

moment.locale('es');
const helmetData = new HelmetData({});

const getAgeLabel = (val) => {
    const map = { 0: 'Todas las edades', 1: 'Menores de 18 acompañados', 2: '18+', 5: '21+', 3: 'Otro' };
    return map[val] ?? val;
};

const getCapacityLabel = (val) => {
    const map = { 0: 'No, solo de pie', 1: 'Por orden de llegada', 2: 'Disponibles con compra VIP', 3: 'Otro' };
    return map[val] ?? val;
};

export default function EventsDetails() {
    const [event, setEvent] = useState({});
    const [tickets, setTickets] = useState([]);

    const [show, setShow] = useState(false);
    const [success, setSuccess] = useState(false);
    const [message, setMessage] = useState('');

    const [isLoadingData, setIsLoadingData] = useState(false);

    const { id } = useParams();
    const history = useHistory();

    const redireccionar = (route) => {
        history.push(route);
    };

    useEffect(() => {
        handleGetEvent();
        window.scrollTo(0, 0);
    }, []);

    const handleGetEvent = async () => {
        setIsLoadingData(true);
        let response = await GetEventByID(id);

        if (response.success === true) {
            setEvent(response.data.event);
            setTickets(response.data.tickets);
            createView('event', id, response.data.event.merchant.uuid);
        }
        setIsLoadingData(false);
        window.scrollTo(0, 0);
    };

    const handleCopy = () => {
        const url = `${domain}eventos/${event.id}?event=${formatStringWithHyphens(event.name)}`;
        navigator.clipboard.writeText(url);

        setSuccess(true);
        setMessage('Enlace copiado exitosamente');
        setShow(true);
    };

    const getImageUrl = (imagePath) => {
        if (!imagePath) return '';
        return imagePath.startsWith('http') ? imagePath : `${imagesURL}eventos/${imagePath}`;
    };

    if (!event.id && !isLoadingData) return null;

    const eventURL = event.id ? `${domain}eventos/${event.id}?event=${formatStringWithHyphens(event.name)}` : '';
    const eventCoverUrl = getImageUrl(event.image);

    // 1. Filtramos para obtener SOLO los tickets con status 1
    const activeTickets = tickets.filter(ticket => ticket.status === 1);

    // 2. Basamos los cálculos estrictamente en los tickets activos
    const getMinPrice = () => {
        if (!activeTickets || activeTickets.length === 0) return 0;
        const min = Math.min(...activeTickets.map(t => Number(t.price)));
        return min.toFixed(2);
    };

    const getTotalAvailable = () => {
        if (!activeTickets || activeTickets.length === 0) return 0;
        return activeTickets.reduce((sum, t) => sum + Number(t.available), 0);
    };

    return (
        <Box sx={{ minHeight: '100vh', pb: 10 }}>
            {event.id &&
                <Helmet helmetData={helmetData}>
                    <title>Eventos - {event.name}</title>
                    <meta name="description" content={event.description} />
                </Helmet>
            }

            <QUDProgress open={isLoadingData} />
            <ResponseComponent
                show={show}
                success={success}
                message={message}
                actionText='Finalizar'
                onClick={() => setShow(false)}
            />

            {/** PORTADA (Basada en tu código original) */}
            {event.id && (
                <Box
                    sx={{
                        backgroundImage: `url(${eventCoverUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'top',
                        width: '100%',
                        display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
                    }}
                >
                    <Box
                        sx={{
                            width: '100%', height: '100%', bgcolor: '#00000090', backdropFilter: 'blur(20px)',
                            display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
                            pt: { xs: 8, md: 12 }, pb: { xs: 6, md: 8 } // Espaciado interno para que respire
                        }}
                    >
                        <Container maxWidth='xl' sx={{ color: '#FFF' }}>
                            <Grid container spacing={3} alignItems={'center'}>
                                {/* Imagen Principal */}
                                <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                                    <Box 
                                        sx={{
                                            width: '100%', position: 'relative', paddingBottom: '100%', 
                                            borderRadius: 4, overflow: 'hidden',
                                            boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
                                        }}
                                    >
                                        <img
                                            style={{
                                                position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                                                objectFit: 'cover'
                                            }}
                                            src={eventCoverUrl}
                                            alt={event.name}
                                        />
                                    </Box>
                                </Grid>

                                {/* Datos del Evento */}
                                <Grid item xs={12} sm={12} md={8} lg={8} xl={8}>
                                    <Stack spacing={3} sx={{ color: 'white' }}>
                                        
                                        {/* Fila del Calendario y Ubicación */}
                                        <Stack direction="row" spacing={3} alignItems="center">
                                            {/* Cuadro de Fecha (Avatar) */}
                                            <Avatar 
                                                variant="rounded" 
                                                sx={{ 
                                                    bgcolor: '#FFF', 
                                                    color: '#1a1a1a', 
                                                    width: 85, 
                                                    height: 85, 
                                                    display: 'flex', 
                                                    flexDirection: 'column', 
                                                    borderRadius: 3,
                                                    boxShadow: '0 4px 15px rgba(0,0,0,0.4)'
                                                }}
                                            >
                                                <Typography variant='subtitle1' fontWeight={'bold'} sx={{ textTransform: 'lowercase', lineHeight: 1, mt: 1 }}>
                                                    {moment(event.date).format('MMM')}.
                                                </Typography>
                                                <Typography variant='h4' fontWeight={'900'} sx={{ lineHeight: 1, mt: 0.5 }}>
                                                    {moment(event.date).format('D')}
                                                </Typography>
                                            </Avatar>

                                            {/* Textos de Ciudad y Dirección */}
                                            <Box>
                                                <Typography variant="h5" fontWeight="500" sx={{ opacity: 0.95 }}>
                                                    {event.city}
                                                </Typography>
                                                <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', opacity: 0.8, mt: 0.5 }}>
                                                    <FontAwesomeIcon icon={faLocationDot} style={{ marginRight: 8 }} /> 
                                                    {event.address}
                                                </Typography>
                                            </Box>
                                        </Stack>

                                        {/* Nombre del Evento */}
                                        <Typography variant="h3" fontWeight="800" sx={{ lineHeight: 1.1 }}>
                                            {event.name}
                                        </Typography>

                                        {/* Barra de Controles (Compartir y Botón) */}
                                        <Box 
                                            sx={{ 
                                                p: { xs: 2, md: 2.5 }, 
                                                borderRadius: 3, 
                                                bgcolor: 'rgba(255, 255, 255, 0.15)', 
                                                backdropFilter: 'blur(10px)',
                                                border: '1px solid rgba(255,255,255,0.2)'
                                            }}
                                        >
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={12} md={8}>
                                                    <Stack direction="row" spacing={2} alignItems="center">
                                                        <Typography variant="body1" fontWeight="700">Compartir</Typography>
                                                        <FacebookShareButton url={eventURL}><FontAwesomeIcon icon={faFacebookF} /></FacebookShareButton>
                                                        <TwitterShareButton url={eventURL}><FontAwesomeIcon icon={faTwitter} /></TwitterShareButton>
                                                        <WhatsappShareButton url={eventURL}><FontAwesomeIcon icon={faWhatsapp} /></WhatsappShareButton>
                                                        <TelegramShareButton url={eventURL}><FontAwesomeIcon icon={faTelegram} /></TelegramShareButton>
                                                        <IconButton size="small" sx={{ color: 'white' }} onClick={handleCopy}>
                                                            <FontAwesomeIcon icon={faLink} size="sm" />
                                                        </IconButton>
                                                    </Stack>
                                                </Grid>
                                                <Grid item xs={12} md={4} display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                                                    <Button 
                                                        variant="contained" 
                                                        color="primary" 
                                                        fullWidth
                                                        disabled={getTotalAvailable() === 0} // Si la suma de los activos es 0, bloqueamos
                                                        onClick={() => redireccionar('/eventos/' + event.id + '/tickets')}
                                                        sx={{ 
                                                            borderRadius: 2, 
                                                            py: 1.5, 
                                                            display: 'flex', 
                                                            flexDirection: 'column', 
                                                            alignItems: 'center',
                                                            boxShadow: '0 2px 4px 0 rgba(0,118,255,0.39)'
                                                        }}
                                                    >
                                                        <Typography variant="body1" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                                                            {getTotalAvailable() === 0 ? 'Agotado' : (event.is_free ? 'Obtener Entradas' : 'Comprar Entradas')}
                                                        </Typography>
                                                        
                                                        {!event.is_free && getTotalAvailable() > 0 && (
                                                            <Typography variant="button" sx={{ textTransform: 'none', opacity: 0.85, mt: 0.5 }}>
                                                                Desde {getMinPrice()} {event?.financial?.base_currency || 'USD'}
                                                            </Typography>
                                                        )}
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Container>
                    </Box>
                </Box>
            )}

            {/** CONTENIDO INFERIOR */}
            <Container maxWidth="xl" sx={{ mt: { xs: 4, md: 8 } }}>
                <Grid container spacing={3}>
                    
                    {/** COLUMNA IZQUIERDA: Detalles rápidos y Tickets */}
                    <Grid item xs={12} md={4}>
                        <Paper variant="outlined" sx={{ p: 3, borderRadius: 4, bgcolor: '#ffffff' }}>
                            <Typography variant="h6" fontWeight="700" mb={3}>Detalles del Evento</Typography>
                            
                            <Stack spacing={3}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="600" textTransform="uppercase">Fecha {event.end_date && 'de Inicio'}</Typography>
                                    <Typography variant="body1" fontWeight="500">{moment(event.date).format("DD [de] MMMM [de] YYYY")}</Typography>
                                </Box>

                                {event.end_date && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" fontWeight="600" textTransform="uppercase">Fecha de Fin</Typography>
                                        <Typography variant="body1" fontWeight="500">{moment(event.end_date).format("DD [de] MMMM [de] YYYY")}</Typography>
                                    </Box>
                                )}

                                <Stack direction="row" spacing={4}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" fontWeight="600" textTransform="uppercase">Apertura</Typography>
                                        <Typography variant="body1" fontWeight="500">{event.opening_time}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" fontWeight="600" textTransform="uppercase">Inicio</Typography>
                                        <Typography variant="body1" fontWeight="500">{event.start_time}</Typography>
                                    </Box>
                                </Stack>

                                <Divider />

                                <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="600" textTransform="uppercase">Restricciones de Edad</Typography>
                                    <Typography variant="body1" fontWeight="500">{getAgeLabel(event.age_limit)}</Typography>
                                </Box>

                                <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="600" textTransform="uppercase">Asientos</Typography>
                                    <Typography variant="body1" fontWeight="500">{getCapacityLabel(event.capacity)}</Typography>
                                </Box>

                                {/* 👇 NUEVA SECCIÓN: LISTA DE TICKETS Y BOTÓN DE COMPRA */}
                    {activeTickets && activeTickets.length > 0 && (
                        <Box sx={{ mt: 2, pt: 3, borderTop: '1px dashed #e0e0e0' }}>
                            <Typography variant="caption" color="text.secondary" fontWeight="600" textTransform="uppercase" display="block" mb={2}>
                                Entradas Disponibles
                            </Typography>
                            
                            <Stack spacing={1.5} mb={3}>
                                {/* Iteramos sobre los tickets filtrados */}
                                {activeTickets.map(ticket => (
                                    <Stack key={ticket.id} direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="body1" fontWeight="500" color="text.primary">
                                            {ticket.name}
                                        </Typography>
                                        <Typography variant="body1" fontWeight="700" color="primary.main">
                                            {event.is_free || ticket.price == 0 
                                                ? 'Gratis' 
                                                : `${Number(ticket.price).toFixed(2)} ${event?.financial?.base_currency || 'USD'}`}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>

                            <Button 
                                variant="contained" 
                                color="primary" 
                                fullWidth
                                disabled={getTotalAvailable() === 0} // Si la suma de los activos es 0, bloqueamos
                                onClick={() => redireccionar('/eventos/' + event.id + '/tickets')}
                                sx={{ 
                                    borderRadius: 2, 
                                    py: 1.5, 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    alignItems: 'center',
                                    boxShadow: '0 4px 14px 0 rgba(0,118,255,0.39)'
                                }}
                            >
                                <Typography variant="body1" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                                    {getTotalAvailable() === 0 ? 'Agotado' : (event.is_free ? 'Obtener Entradas' : 'Comprar Entradas')}
                                </Typography>
                                
                                {!event.is_free && getTotalAvailable() > 0 && (
                                    <Typography variant="button" sx={{ textTransform: 'none', opacity: 0.85, mt: 0.5 }}>
                                        Desde {getMinPrice()} {event?.financial?.base_currency || 'USD'}
                                    </Typography>
                                )}
                            </Button>
                        </Box>
                    )}

                            </Stack>
                        </Paper>

                        {/** Tarjeta de Contacto / Productor (Mantenemos la que ya tenías abajo) */}
                        {event.merchant && (
                            <Paper variant="outlined" sx={{ p: 3, borderRadius: 4, bgcolor: '#ffffff', mt: 3 }}>
                                <Typography variant="h6" fontWeight="700" mb={2}>Organizado por</Typography>
                                <Typography variant="h5" fontWeight="600" color="primary" mb={2}>{event.merchant.name}</Typography>
                                
                                <Stack spacing={1.5}>
                                    <Button 
                                        variant="outlined" 
                                        fullWidth 
                                        sx={{ borderRadius: 2, justifyContent: 'flex-start', py: 1 }}
                                        onClick={() => redireccionar(`/merchants/${event.merchant.uuid}/profile`)}
                                    >
                                        Visitar perfil del organizador
                                    </Button>
                                    <Button 
                                        variant="outlined" 
                                        color="inherit"
                                        fullWidth 
                                        sx={{ borderRadius: 2, justifyContent: 'flex-start', py: 1 }}
                                        href={`https://wa.me/${event.merchant.phone?.replace(/[^0-9]/g, '')}`} 
                                        target="_blank"
                                        startIcon={<FontAwesomeIcon icon={faWhatsapp} color="#25D366" />}
                                    >
                                        Contactar Soporte
                                    </Button>
                                </Stack>
                            </Paper>
                        )}
                    </Grid>

                    {/** COLUMNA DERECHA: Descripción, Video y Mapa */}
                    <Grid item xs={12} md={8}>
                        <Paper variant="outlined" sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, bgcolor: '#ffffff' }}>
                            <Typography variant="h5" fontWeight="700" mb={3}>Acerca del Evento</Typography>
                            
                            {event.description && (
                                <Box sx={{ 
                                    fontSize: '1.05rem', 
                                    color: 'text.primary', // Quitamos el secondary
                                    lineHeight: 1.7, 
                                    mb: 4,
                                    // Forzamos a que cualquier título o negrita que venga del editor se vea oscuro y fuerte
                                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                                        color: '#1a1a1a !important',
                                        fontWeight: '800',
                                        mb: 1
                                    },
                                    '& strong': { color: '#1a1a1a' }
                                }}>
                                    <ReactQuill 
                                        className="my-quill-container-clean"
                                        theme={'bubble'} 
                                        readOnly={true} 
                                        value={event.description}
                                    />
                                </Box>
                            )}

                            {event.youtube_url && (
                                <Box sx={{ width: '100%', borderRadius: 3, overflow: 'hidden', mb: 4, aspectRatio: '16/9' }}>
                                    <ReactPlayer width="100%" height="100%" url={event.youtube_url} />
                                </Box>
                            )}

                            <Divider sx={{ my: 4 }} />

                            <Typography variant="h5" fontWeight="700" mb={2}>Ubicación</Typography>
                            <Typography variant="body1" color="text.secondary" mb={2}>{event.address}</Typography>
                            
                            <Button
                                variant="contained"
                                color="link" 
                                startIcon={<FontAwesomeIcon icon={faDirections} />}
                                target="_blank"
                                sx={{ borderRadius: 8, px: 3 }}
                                href={`http://googleusercontent.com/maps.google.com/maps?q=${encodeURIComponent(event.address)}`}
                            >
                                Obtener indicaciones
                            </Button>
                        </Paper>
                    </Grid>

                </Grid>
            </Container>
        </Box>
    );
}